import {useEffect, useState} from 'react'
import type {AgentMention} from '../types/agent-mention'
import {createPendingMention} from '../types/agent-mention'
import type {Session} from '../types/session'
import {SessionState} from '../types/session'
import type {PublicUser} from '../types/public-user'

interface UsePendingSessionParams {
  pullId?: number
  taskId?: string
  sessions?: Session[]
  mentions?: AgentMention[]
  currentUser?: PublicUser
}

interface CreatePendingSessionOptions {
  agentId?: number
  isPRComment?: boolean
  taskId?: string
}

interface AddPendingSessionOptions {
  agentId?: number
  isPRComment?: boolean
}

export function createPendingSession(
  pullId: number | undefined,
  textOrMentionId: string,
  currentUserId?: number,
  options?: CreatePendingSessionOptions,
): Session {
  const now = new Date().toISOString()
  const {agentId, isPRComment = true, taskId} = options ?? {}

  return {
    id: `pending-session`,
    name: 'New session',
    user_id: currentUserId ?? 0,
    agent_id: agentId ?? 0,
    state: SessionState.Queued,
    owner_id: 0,
    repo_id: 0,
    resource_type: 'pull',
    resource_id: pullId ?? 0,
    resource_global_id: '',
    created_at: now,
    last_updated_at: now,
    updated_at: now,
    completed_at: undefined,
    workflow_run_id: 0,
    log_entries: [],
    error: null,
    premium_requests: 0,
    event_identifiers: isPRComment ? [textOrMentionId] : null,
    event_type: isPRComment ? 'pull_request_comment' : 'api_call_received',
    event_url: null,
    event_content: isPRComment ? null : textOrMentionId,
    isPending: true,
    model: '',
    task_id: taskId,
  }
}

export function usePendingSession({
  pullId,
  taskId,
  sessions = [],
  mentions = [],
  currentUser,
}: UsePendingSessionParams) {
  const [pendingSession, setPendingSession] = useState<Session | null>(null)
  const [pendingMention, setPendingMention] = useState<AgentMention | null>(null)

  const pendingSessionMatchesTask = pendingSession?.task_id === taskId

  // Track previous counts to detect new items without using refs during render
  const [prevSessionCount, setPrevSessionCount] = useState(sessions.length)
  const [prevMentionCount, setPrevMentionCount] = useState(mentions.length)

  // Determine if new items have arrived
  const hasNewSessions = sessions.length > prevSessionCount
  const hasNewMentions = mentions.length > prevMentionCount

  // Update previous counts in a separate effect to avoid setting state during render
  useEffect(() => {
    // Only update if this isn't the initial render (values already match)
    if (sessions.length !== prevSessionCount) {
      // Use timeout to avoid immediate state updates during the render phase
      const timeoutId = setTimeout(() => {
        setPrevSessionCount(sessions.length)
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [sessions.length, prevSessionCount])

  useEffect(() => {
    // Only update if this isn't the initial render (values already match)
    if (mentions.length !== prevMentionCount) {
      // Use timeout to avoid immediate state updates during the render phase
      const timeoutId = setTimeout(() => {
        setPrevMentionCount(mentions.length)
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [mentions.length, prevMentionCount])

  // Filter pending items (hide when new real items arrive or task changed)
  // This is needed to avoid flicker because the useEffect below defers cleanup until after render
  const filteredPendingSession = hasNewSessions || !pendingSessionMatchesTask ? null : pendingSession
  const filteredPendingMention = hasNewMentions || !pendingSessionMatchesTask ? null : pendingMention

  // Clear pending states when they're no longer needed
  useEffect(() => {
    if ((hasNewSessions && pendingSession !== null) || (hasNewMentions && pendingMention !== null)) {
      // Using immediate timeout to defer cleanup until after render
      const timeoutId = setTimeout(() => {
        if (hasNewSessions) setPendingSession(null)
        if (hasNewMentions) setPendingMention(null)
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [hasNewSessions, hasNewMentions, pendingSession, pendingMention])

  const addPendingSession = (text: string, options: AddPendingSessionOptions = {}) => {
    const {agentId, isPRComment = true} = options
    if (isPRComment) {
      const newPendingMention = createPendingMention(text, currentUser)
      setPendingMention(newPendingMention)
      const newPendingSession = createPendingSession(pullId, newPendingMention.id.toString(), currentUser?.id, {
        agentId,
        isPRComment,
        taskId,
      })
      setPendingSession(newPendingSession)
    } else {
      const newPendingSession = createPendingSession(pullId, text, currentUser?.id, {agentId, isPRComment, taskId})
      setPendingSession(newPendingSession)
    }
  }

  // Clears the pending session and mention
  const clearPendingSession = () => {
    setPendingSession(null)
    setPendingMention(null)
  }

  // Combine real sessions with pending session
  const allSessions = filteredPendingSession ? [...sessions, filteredPendingSession] : sessions
  const allMentions = filteredPendingMention ? [...mentions, filteredPendingMention] : mentions

  return {
    allSessions,
    allMentions,
    addPendingSession,
    clearPendingSession,
  }
}
